import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { useCurrencies, useSearchCurrency } from 'features/finance';
import { useWithdraws } from '../api/getWithdraws';
import { useDeposits } from '../api/getDeposits';
import { useTransfers } from '../api/getTransfers';
import { useSwaps } from '../api/getSwaps';

export const useTransactions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeCurrency, setActiveCurrency] = useState(null);
  const [transactions, setTransactions] = useState({
    withdraws: [],
    deposits: [],
    transfers: [],
    swaps: [],
  });

  const { data: currenciesData, isLoading: isCurrenciesLoading } =
    useCurrencies({ pageNumber: 1 });

  const currencyId = activeCurrency?.id || null;

  // Fetch transactions based on activeCurrency ID
  const { data: withdrawsData, refetch: refetchWithdraws } = useWithdraws({
    currencyId,
    pageNumber: 1,
  });

  const { data: depositsData, refetch: refetchDeposits } = useDeposits({
    currencyId,
    pageNumber: 1,
  });

  const { data: transfersData, refetch: refetchTransfers } = useTransfers({
    currencyId,
    pageNumber: 1,
  });

  const { data: swapsData, refetch: refetchSwaps } = useSwaps({
    currencyId,
    pageNumber: 1,
  });

  const {
    data: currenciesSearchQueryData,
    isLoading: isCurrenciesSearchLoading,
    refetch: refetchCurrencies,
  } = useSearchCurrency({
    text: searchTerm.trim().length > 0 ? searchTerm : null,
  });

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]); // Reset when input is cleared
    } else if (currenciesSearchQueryData?.currencies) {
      setSearchResults(currenciesSearchQueryData.currencies);
    }
  }, [currenciesSearchQueryData]);

  useEffect(() => {
    if (currenciesData?.currencies?.length > 0 && !activeCurrency) {
      const initialCurrency = currenciesData.currencies[0];
      console.log('Setting Initial Active Currency:', initialCurrency);
      setActiveCurrency(() => initialCurrency); // Ensure proper state update
    }
  }, [currenciesData]);

  // Fetch transactions when activeCurrency changes
  useEffect(() => {
    if (currencyId) {
      console.log('Fetching transactions for currency:', activeCurrency);
      refetchWithdraws();
      refetchDeposits();
      refetchTransfers();
      refetchSwaps();
    }
  }, [currencyId]);

  // Store transaction data when fetched
  useEffect(() => {
    setTransactions({
      withdraws: withdrawsData || [],
      deposits: depositsData || [],
      transfers: transfersData || [],
      swaps: swapsData || [],
    });
  }, [withdrawsData, depositsData, transfersData, swapsData]);

  const handleSetActiveCurrency = (currency) => {
    if (!currency) return;
    console.log('Changing Active Currency to:', currency);
    setActiveCurrency(() => currency); // Ensure immediate state update
  };

  const handleSearch = useCallback(
    debounce((query) => {
      setSearchTerm(query);
      if (query.trim() === '') {
        setSearchResults([]); // Reset results when input is empty
      } else {
        refetchCurrencies();
      }
    }, 300),
    []
  );

  return {
    searchTerm,
    searchResults,
    isCurrenciesLoading,
    isCurrenciesSearchLoading,
    activeCurrency,
    setActiveCurrency: handleSetActiveCurrency,
    handleSearch,
    currenciesData,
    transactions,
  };
};
